function updateOettingerCartBubble(itemCount) {
  const cartLink = document.getElementById('cart-icon-bubble');
  if (!cartLink || !cartLink.querySelector('svg')) return;

  const bubble = cartLink.querySelector('.cart-count-bubble');
  if (!bubble) return;

  const normalizedCount = Number(itemCount);
  if (!Number.isFinite(normalizedCount)) return;

  bubble.hidden = normalizedCount < 1;

  let visibleCount = bubble.querySelector('[aria-hidden="true"]');
  if (!visibleCount) {
    visibleCount = document.createElement('span');
    visibleCount.setAttribute('aria-hidden', 'true');
    bubble.prepend(visibleCount);
  }

  visibleCount.textContent = normalizedCount < 100 ? normalizedCount : '99+';

  const hiddenCount = bubble.querySelector('.visually-hidden');
  if (hiddenCount) hiddenCount.textContent = `${normalizedCount} Artikel`;
}

window.updateOettingerCartBubble = updateOettingerCartBubble;

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.onOutsideClick = this.handleOutsideClick.bind(this);
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.addEventListener('click', this.onOutsideClick);
    this.addEventListener('change', (event) => {
      if (event.target.matches('#CartDrawer-Terms')) this.updateOettingerCheckoutState();
    });
    this.setHeaderCartIconAccessibility();
    this.updateOettingerCheckoutState();
  }

  updateOettingerCheckoutState() {
    if (!this.classList.contains('oettinger-cart-drawer')) return;

    const termsCheckbox = this.querySelector('#CartDrawer-Terms');
    const checkoutButton = this.querySelector('#CartDrawer-Checkout');
    if (!termsCheckbox || !checkoutButton) return;

    checkoutButton.disabled = !termsCheckbox.checked;
    window.oettingerShippingGuard?.validate();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const drawerInner = this.querySelector('.drawer__inner');
        const isOettingerEmptyDrawer =
          this.classList.contains('is-empty') && this.classList.contains('oettinger-cart-drawer');
        const containerToTrapFocusOn = isOettingerEmptyDrawer
          ? drawerInner
          : this.classList.contains('is-empty')
            ? this.querySelector('.drawer__inner-empty')
            : document.getElementById('CartDrawer');
        const focusElement =
          (isOettingerEmptyDrawer && this.querySelector('.drawer__header .drawer__close')) ||
          drawerInner ||
          this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  handleOutsideClick(event) {
    if (!this.classList.contains('active')) return;
    if (event.target.closest('.drawer__inner')) return;

    this.close();
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;
    return this.refreshCartDrawer(parsedState);
  }

  renderSections(parsedState) {
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = document.getElementById(section.id);
      const sectionHtml = parsedState.sections[section.section || section.id];

      if (!sectionElement || !sectionHtml) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);
    });
  }

  afterRenderContents(itemCount) {
    const normalizedItemCount = Number.isFinite(Number(itemCount)) ? Number(itemCount) : this.getOettingerCartItemCount();

    this.classList.toggle('is-empty', normalizedItemCount === 0);
    this.querySelector('.drawer__inner')?.classList.toggle('is-empty', normalizedItemCount === 0);
    updateOettingerCartBubble(normalizedItemCount);

    setTimeout(() => {
      this.updateOettingerCheckoutState();
      window.oettingerShippingGuard?.validate();
      this.open();
    });
  }

  refreshCartDrawer(parsedState) {
    return Promise.all([
      fetch(`${routes.cart_url}.js`, { cache: 'no-store' }).then((response) => response.json()),
      this.fetchCartDrawerSections(),
    ])
      .then(([cart, sections]) => {
        const sectionHtml = sections?.['cart-drawer'];

        if (!this.replaceCartDrawerHTML(sectionHtml)) {
          this.renderSections({
            sections: {
              'cart-drawer': sectionHtml,
              'cart-icon-bubble': sections?.['cart-icon-bubble'],
            },
          });
        } else if (sections?.['cart-icon-bubble']) {
          const cartBubble = document.getElementById('cart-icon-bubble');
          if (cartBubble) cartBubble.innerHTML = this.getSectionInnerHTML(sections['cart-icon-bubble']);
        }

        this.afterRenderContents(cart?.item_count);
      })
      .catch((error) => {
        console.error(error);
        if (!parsedState?.sections?.['cart-drawer']) return;

        this.renderSections(parsedState);
        this.afterRenderContents(parsedState.item_count);
      });
  }

  fetchCartDrawerSections() {
    return fetch(`${routes.cart_url}?sections=cart-drawer,cart-icon-bubble`, { cache: 'no-store' })
      .then((response) => response.text())
      .then((responseText) => {
        const sections = JSON.parse(responseText);
        if (sections?.['cart-drawer']) return sections;
        throw new Error('Cart drawer section was not returned.');
      })
      .catch(() => {
        return fetch(`${routes.cart_url}?section_id=cart-drawer`, { cache: 'no-store' })
          .then((response) => response.text())
          .then((sectionHtml) => ({
            'cart-drawer': sectionHtml,
          }));
      });
  }

  replaceCartDrawerHTML(sectionHtml) {
    const currentDrawer = this.querySelector('#CartDrawer');
    const incomingDrawer = this.getSectionDOM(sectionHtml, '#CartDrawer');

    if (!currentDrawer || !incomingDrawer) return false;

    currentDrawer.innerHTML = incomingDrawer.innerHTML;
    return true;
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    return (parsedHTML.querySelector(selector) || parsedHTML.body).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  getOettingerCartItemCount() {
    return [...this.querySelectorAll('#CartDrawer-CartItems .quantity__input')].reduce((count, input) => {
      return count + (Number(input.value) || 0);
    }, 0);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
